/**
 * Pure helpers for the Become a Partner form (COM-737). No I/O — the client
 * file and the server module both build on these.
 */
import {
  PARTNER_FORM_EMAIL_PATTERN,
  PARTNER_FORM_ERRORS,
} from "./partner-form.schemas";
import type {
  PartnerFormFailureReason,
  PartnerFormResult,
  PartnerFormStatus,
  PartnerFormValues,
} from "./types";

/** The SFRA controller the Worker bridges to (`org_hec/cartridge/controllers/PartnerForm.js`). */
export const PARTNER_FORM_CONTROLLER = "PartnerForm-Submit";

/** Same-origin resource route the browser posts to. */
export const PARTNER_FORM_ENDPOINT = "/resources/partner-form";

/** The one live check before Submit (AC). Format only: blank is a Submit-time failure. */
export function livePartnerEmailError(value: string): string | null {
  // Trimmed like the schema. Browsers already strip it for `type="email"`; this
  // keeps the check honest if the field is ever rendered as plain text.
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return PARTNER_FORM_EMAIL_PATTERN.test(trimmed)
    ? null
    : PARTNER_FORM_ERRORS.email;
}

/**
 * `PartnerForm-Submit` reads `req.form.{fullName,companyName,jobTitle,email,
 * phone,message}` — the same names the form uses, so the browser body and the
 * SFRA body are one builder. The CSRF pair is added by `sfraBridge`.
 */
export function buildPartnerFormBody(
  values: PartnerFormValues,
): URLSearchParams {
  return new URLSearchParams({
    fullName: values.fullName,
    companyName: values.companyName,
    jobTitle: values.jobTitle,
    email: values.email,
    phone: values.phone,
    message: values.message,
  });
}

const FAILURE_REASONS: readonly PartnerFormFailureReason[] = [
  "invalid",
  "rate_limited",
  "unavailable",
];

/** Anything that is not exactly the contract is `unavailable`, never a success. */
export function parsePartnerFormResult(json: unknown): PartnerFormResult {
  if (typeof json === "object" && json !== null && "ok" in json) {
    if (json.ok === true) return { ok: true };
    const reason = "reason" in json ? json.reason : undefined;
    if (FAILURE_REASONS.includes(reason as PartnerFormFailureReason)) {
      return { ok: false, reason: reason as PartnerFormFailureReason };
    }
  }
  return { ok: false, reason: "unavailable" };
}

export function statusForPartnerResult(
  result: PartnerFormResult,
): PartnerFormStatus {
  return result.ok
    ? { kind: "succeeded" }
    : { kind: "failed", reason: result.reason };
}
